import { type ComponentPropsWithoutRef, createElement, type ElementType } from 'react';

import { DEFAULT_PORTAL_ID } from '#portal/constants';

import type { PortalOutletProps } from './types/portal-outlet.props';

/**
 * Renders a portal outlet component that acts as a container for portal content.
 *
 * @template TElement - The type of the HTML element or React component to render.
 * @param as - The element type to render. Defaults to `'div'` if not specified.
 * @param children - The content to be rendered inside the portal outlet.
 * @param id - The id attribute for the portal outlet. Defaults to `DEFAULT_PORTAL_ID`.
 * @param ref - Ref forwarded to the rendered element.
 * @param props - Additional props passed to the rendered element.
 * @returns A React element of the specified type containing the provided children.
 */
export const PortalOutlet = <TElement extends ElementType = 'div'>({
  as,
  children,
  id = DEFAULT_PORTAL_ID,
  ref,
  ...props
}: PortalOutletProps<TElement>) => {
  const Element = as ?? ('div' as TElement);

  return createElement(
    Element,
    {
      ...(props as ComponentPropsWithoutRef<TElement>),
      id,
      ref,
    },
    children
  );
};
